import { useEffect, useReducer, useState } from 'react'

import { usePlayerSeasonStats } from '../../hooks/usePlayerSeasonStats'
import { ALL_SPORTS, GAMES_STAT_ABBR, GAMES_STAT_NAME } from '../constants'
import type {
  CareerHighlight,
  ChartPoint,
  ProgressBucket,
  ProgressComparison,
  ProgressSport,
  RecentGameCardModel,
  SeasonGridRow,
  StatChart,
  StatColumn,
  StatsRole,
  StatsSelection,
  StatsSelectionAction,
  ThisWeekSummary,
} from '../types'
import { buildCareerHighlights } from '../utils/careerHighlights'
import { buildCareerRow } from '../utils/careerRow'
import {
  type FrozenDefaults,
  pickProgressDefaults,
  resolveFrozenDefaults,
} from '../utils/defaultStats'
import {
  buildProgressChart,
  listProgressChips,
  sortGamesFirst,
} from '../utils/progressChart'
import { buildRecentGameCard } from '../utils/recentGameCard'
import { buildStatColumns } from '../utils/seasonColumns'
import { buildStatNames } from '../utils/statNames'
import { buildSeasonGridRows, getOpenScheduleId } from '../utils/seasonGridRows'
import {
  INITIAL_SELECTION,
  resolveStatsSelection,
  statsSelectionReducer,
} from '../utils/selection'
import {
  getSportKey,
  getSportLabel,
  resolveSportQuery,
} from '../utils/sportKey'
import { buildThisWeekSummary } from '../utils/thisWeekSummary'
import { usePlayerProgress } from './usePlayerProgress'
import { usePlayerProgressSports } from './usePlayerProgressSports'
import { usePlayerRecentGames } from './usePlayerRecentGames'
import { usePlayerScheduleGames } from './usePlayerScheduleGames'

/** `isLoading` is cold load only — a background refetch keeps `data`. */
export interface SectionState<T> {
  data: T | undefined
  isLoading: boolean
  error: Error | null
}

export interface SportOption {
  value: string
  label: string
}

export interface ProgressSectionData {
  /**
   * One chart per selected stat, in selection order, each with its colour
   * slot; empty for All Sports.
   */
  charts: StatChart[]
  chartStats: string[]
  /** Charted before the user picks any (`pickDefaultStats`). */
  defaultStats: string[]
  bucket: ProgressBucket
  gamesChart: ChartPoint[]
  /** The range against the window before it; null when the API sent none. */
  comparison: ProgressComparison | null
}

export interface GridSectionData {
  columns: StatColumn[]
  careerRow: Record<string, string>
  rows: SeasonGridRow[]
  /** The expanded season's games failed to load — shown in that row only. */
  openGamesError: Error | null
}

export interface PlayerStatsTabModel {
  selection: StatsSelection
  dispatch: (action: StatsSelectionAction) => void
  sportOptions: SportOption[]
  /** The selected sport's `name` (e.g. 'hockey'); null for All Sports. */
  sportName: string | null
  roles: StatsRole[]
  isAllSports: boolean
  /**
   * Recent Games (and the This Week last-played line) are shown. False for
   * All Sports and for a specific generic sport — `/game-stats/recent` can't
   * filter by generic sport, so its games would mix other generic sports.
   */
  showRecentGames: boolean
  /** Abbr → full stat name for the selected sport and role (from the season rows). */
  statNames: Record<string, string>
  sportsState: SectionState<ProgressSport[]>
  progress: SectionState<ProgressSectionData>
  /**
   * The This Week strip. A sport with no games this week also waits for the
   * recent games' cold load, so the last-played line never pops in late.
   */
  thisWeek: SectionState<ThisWeekSummary>
  recent: SectionState<RecentGameCardModel[]>
  grid: SectionState<GridSectionData>
  /**
   * Games played and the role's card stats from the career row, for a header;
   * empty for All Sports and until the season rows load.
   */
  careerHighlights: CareerHighlight[]
}

export interface UsePlayerStatsTabArgs {
  playerId?: string
  timezone: string
}

const ALL_SPORTS_LABEL = 'All Sports'
const GAMES_PLAYED_ABBR = 'GP'
const EMPTY_SPORTS: ProgressSport[] = []

const toSportOptions = (sports: ProgressSport[]): SportOption[] =>
  sports.length === 0
    ? []
    : [
        { value: ALL_SPORTS, label: ALL_SPORTS_LABEL },
        ...sports.map((s) => ({
          value: getSportKey(s),
          label: getSportLabel(s),
        })),
      ]

const toRoles = (hasSkater: boolean, hasGoalie: boolean): StatsRole[] => {
  const roles: StatsRole[] = [
    ...(hasSkater ? (['skater'] as const) : []),
    ...(hasGoalie ? (['goalie'] as const) : []),
  ]
  return roles.length ? roles : ['skater']
}

/**
 * View model for the player Stats tab, shared by web and mobile.
 *
 * Selection is resolved in stages because each query depends on the previous
 * resolution: sports → sport, season rows → role, progress series → stats. The
 * sport only depends on `sports` and the role only on `roles`, so the final
 * resolve agrees with the values the queries were keyed on.
 */
export const usePlayerStatsTab = ({
  playerId,
  timezone,
}: UsePlayerStatsTabArgs): PlayerStatsTabModel => {
  'use memo'

  const [rawSelection, dispatch] = useReducer(
    statsSelectionReducer,
    INITIAL_SELECTION
  )
  const [frozenDefaults, setFrozenDefaults] = useState<FrozenDefaults | null>(
    null
  )

  // The one render before this runs is harmless: the sports query is keyed by
  // playerId, so `sports` is empty, sportKey resolves to null, and every
  // downstream query is disabled.
  useEffect(() => {
    dispatch({ type: 'reset' })
  }, [playerId])

  const sportsQuery = usePlayerProgressSports(playerId)
  const sports = sportsQuery.data ?? EMPTY_SPORTS

  const { sportKey } = resolveStatsSelection({
    selection: rawSelection,
    sports,
    roles: ['skater'],
    chartStats: [],
  })
  const { sport, sportId, genericSportId } = resolveSportQuery(sports, sportKey)
  const isAllSports = sportId === ALL_SPORTS
  const showRecentGames = !isAllSports && genericSportId === undefined
  const seasonSportId = isAllSports ? undefined : sportId ?? undefined

  const seasonStats = usePlayerSeasonStats({
    playerId,
    sportId: seasonSportId,
    genericSportId,
    isSkater: true,
    isGoalie: true,
    enabled: !isAllSports,
  })
  const roles = toRoles(
    seasonStats.playerStats.length > 0,
    seasonStats.goalieStats.length > 0
  )

  const { role } = resolveStatsSelection({
    selection: rawSelection,
    sports,
    roles,
    chartStats: [],
  })

  // Role-dependent queries wait for season stats so a goalie-only player never
  // fetches (or flashes) the placeholder skater series. All Sports has no role.
  const isRoleResolving = !isAllSports && seasonStats.isLoading
  const roleReady = isAllSports || (sportId !== null && !seasonStats.isLoading)

  const progressQuery = usePlayerProgress({
    playerId,
    sportId,
    genericSportId,
    role,
    range: rawSelection.range,
    timezone,
    enabled: roleReady,
  })
  const recentQuery = usePlayerRecentGames({
    playerId,
    sportId,
    role,
    enabled: roleReady && showRecentGames,
  })
  const seasons =
    role === 'goalie' ? seasonStats.goalieStats : seasonStats.playerStats
  const scheduleQuery = usePlayerScheduleGames({
    playerId,
    scheduleId: getOpenScheduleId(seasons, rawSelection.openSeasonRowId),
    role,
  })

  const series = progressQuery.data?.series ?? []
  const chartStats = listProgressChips(series)
  // Picked once per player + sport + role (from the first range that loads)
  // and kept across range changes, so the preselected stats never reshuffle.
  // Stored by adjusting state during render — React's pattern for a value
  // derived from earlier renders.
  const { stats: defaultStats, next: nextDefaults } = resolveFrozenDefaults({
    frozen: frozenDefaults,
    key: `${playerId}:${sportKey}:${role}`,
    picked: pickProgressDefaults(series),
    isReady:
      progressQuery.data !== undefined && !progressQuery.isPlaceholderData,
  })
  if (nextDefaults) {
    setFrozenDefaults(nextDefaults)
  }
  const selection = resolveStatsSelection({
    selection: rawSelection,
    sports,
    roles,
    chartStats,
    defaultStats,
  })

  const progressData = progressQuery.data
  const progressSection: ProgressSectionData | undefined = progressData
    ? {
        charts: isAllSports
          ? []
          : sortGamesFirst(selection.stats, (abbr) => abbr).map((abbr) => ({
              abbr,
              colorSlot: selection.colorSlots[abbr],
              chart: buildProgressChart(series, abbr, progressData.bucket),
            })),
        chartStats,
        defaultStats,
        bucket: progressData.bucket,
        gamesChart: buildProgressChart(
          series,
          GAMES_PLAYED_ABBR,
          progressData.bucket
        ).points,
        comparison: progressData.comparison ?? null,
      }
    : undefined

  // Guarded here too: plain Generic shares the recent-games key, so its cached
  // games would otherwise surface for a specific generic sport.
  const recentSection: RecentGameCardModel[] | undefined = showRecentGames
    ? recentQuery.data?.map((g) => buildRecentGameCard(g, role, timezone))
    : undefined

  const columns = buildStatColumns(seasons)
  const statNames = {
    [GAMES_STAT_ABBR]: GAMES_STAT_NAME,
    ...buildStatNames(columns),
  }
  const thisWeekData: ThisWeekSummary | undefined = progressData
    ? buildThisWeekSummary({
        thisWeek: progressData.this_week,
        series: progressData.series,
        streakWeeks: progressData.streak_weeks,
        isAllSports,
        lastGame: recentSection?.[0],
        statNames,
      })
    : undefined
  const isAwaitingLastGame =
    showRecentGames && progressData !== undefined && recentQuery.isLoading

  const gridSection: GridSectionData | undefined =
    seasonSportId === undefined || seasonStats.isLoading
      ? undefined
      : {
          columns,
          careerRow: buildCareerRow(seasons, columns),
          rows: buildSeasonGridRows({
            seasons,
            columns,
            openSeasonRowId: rawSelection.openSeasonRowId,
            openGames: scheduleQuery.data,
            openGamesError: scheduleQuery.error,
            timezone,
          }),
          openGamesError: scheduleQuery.error,
        }

  return {
    selection,
    dispatch,
    sportOptions: toSportOptions(sports),
    sportName: sport?.name ?? null,
    roles,
    isAllSports,
    showRecentGames,
    statNames,
    sportsState: {
      data: sportsQuery.data,
      isLoading: sportsQuery.isLoading,
      error: sportsQuery.error,
    },
    progress: {
      data: progressSection,
      isLoading: progressQuery.isLoading || isRoleResolving,
      error: progressQuery.error,
    },
    thisWeek: {
      data: thisWeekData,
      isLoading:
        progressQuery.isLoading || isRoleResolving || isAwaitingLastGame,
      error: progressQuery.error,
    },
    recent: {
      data: recentSection,
      isLoading: recentQuery.isLoading || isRoleResolving,
      error: recentQuery.error,
    },
    grid: {
      data: gridSection,
      isLoading: seasonStats.isLoading,
      error: seasonStats.error,
    },
    careerHighlights: gridSection
      ? buildCareerHighlights(gridSection.columns, gridSection.careerRow, role)
      : [],
  }
}

export default usePlayerStatsTab
